import React, { useContext, useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Slider,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import { createReport } from "../../../services/stixify_api.ts";
import LoadingButton from "../../../components/loading_button/index.tsx";
import { useAlert } from "../../../contexts/alert-context.tsx";
import LabelInput from "./label-input.tsx";
import { TeamContext } from "../../../contexts/team-context.tsx";
import UploadSuccessDialog from "./upload-success-dialog.tsx";
import { Link } from "react-router-dom";
import { URLS } from "../../../services/urls.ts";

const TLP_LEVELS = ["amber", "green"];

const MIME_TYPE_DICT = {
  "text/plain": "txt",
  "text/markdown": "txt",
  "image/jpg": "image",
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
  "text/csv": "csv",
  "text/html": "html_article",
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "word",
  "application/msword": "word",
  "application/vnd.ms-powerpoint": "powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "powerpoint",
  "application/vnd.ms-excel": "excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "excel",
  "application/vnd.ms-excel.sheet.binary.macroenabled.12": "excel",
};

const MODES = [
  "txt",
  "image",
  "csv",
  "html",
  "html_article",
  "word",
  "pdf",
  "powerpoint",
  "excel",
];

interface ReportFormProps {
  onCancel: () => void;
  onComplete: () => void;
  entryData?: any;
}

const ReportForm: React.FC<ReportFormProps> = ({
  onCancel,
  onComplete,
  entryData,
}) => {
  const { activeTeam } = useContext(TeamContext);
  const [loading, setLoading] = useState(false);
  const alert = useAlert();
  const [formData, setFormData] = useState<{
    name: string;
    mode: string;
    tlp_level: string;
    confidence: number;
    labels: string[];
    relationship_mode: string;
    ai_create_attack_flow: boolean;
    extract_text_from_image: boolean;
  }>({
    name: "",
    mode: "",
    tlp_level: "green",
    confidence: 50,
    labels: [],
    relationship_mode: "standard",
    ai_create_attack_flow: false,
    extract_text_from_image: false,
  });

  const [errors, setErrors] = useState<{
    file?: string[];
    name?: string[];
    mode?: string[];
    tlp_level?: string[];
    confidence?: string[];
    labels?: string[];
    relationship_mode?: string[];
    ai_create_attack_flow?: string[];
    extract_text_from_image?: string[];
    fileText?: string[];
    non_field_errors?: string[];
  }>({
    file: [],
    name: [],
    mode: [],
    tlp_level: [],
    confidence: [],
    labels: [],
    relationship_mode: [],
    ai_create_attack_flow: [],
    extract_text_from_image: [],
    fileText: [],
  });
  const [disableButton, setDisableButton] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File>();
  const [limitExhausted, setLimitExhausted] = useState(false);
  const [fileId, setFileId] = useState<string>();
  const [jobId, setJobId] = useState<string>();
  const [useFile, setUseFile] = useState(true);
  const [fileText, setFileText] = useState("");

  useEffect(() => {
    setLimitExhausted(
      (activeTeam?.allowed_reports_per_month || 0) <=
        (activeTeam?.month_upload_count || 0)
    );
  }, [activeTeam]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const mode = MIME_TYPE_DICT[file.type];
      if (!mode) {
        event.target.value = "";
        await alert.showAlert("Selected file not supported", "error");
        return;
      }
      setSelectedFile(file);
      setFormData({ ...formData, mode });
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(undefined);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addEntry = async (data: any) => {
    if (useFile && !selectedFile) {
      setErrors({ file: ["File is required"] });
      return;
    }
    if (!useFile && !fileText) {
      setErrors({ fileText: ["Text is required"] });
      return;
    }
    const file = useFile ? selectedFile : convertTextToFile();
    const res = await createReport(activeTeam?.id || "", file, {
      ...data,
    });
    setJobId(res.data.stixify_job_metadata.id);
    setFileId(res.data.stixify_file_metadata.id);
  };

  useEffect(() => {
    let disable = false;
    if (!formData.name) setDisableButton(disable);
  }, [formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { ...submitData } = formData;
    setLoading(true);
    try {
      if (submitData.relationship_mode === "standard") {
        submitData.ai_create_attack_flow = false;
        submitData.extract_text_from_image = false;
      }
      const response = await addEntry(submitData);
    } catch (err) {
      if (err?.response?.status === 400) {
        console.log(err?.response?.data?.details, err?.response?.data, "sxaa");
        setErrors(err?.response?.data?.details || err?.response?.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const setConfidence = (value: any) => {
    setFormData({ ...formData, confidence: value });
  };

  const convertTextToFile = () => {
    const blob = new Blob([fileText], { type: "text/plain" });
    return new File([blob], "file.txt", { type: blob.type });
  };

  return (
    <Box>
      {jobId && fileId && (
        <UploadSuccessDialog
          jobId={jobId}
          fileId={fileId}
          onClose={onComplete}
          teamId={activeTeam?.id || ""}
        ></UploadSuccessDialog>
      )}
      <Typography variant="h4">
        {entryData ? "Update a Report" : "Upload a New Report"}
      </Typography>

      <Typography>
        <p>
          Please enter the details of the Report. Note, it is not possible to
          edit a Report once it is uploaded.
        </p>
      </Typography>

      {limitExhausted ? (
        <>
          <Box
            sx={{
              backgroundColor: "red",
              color: "white",
              padding: "1rem",
              marginBottom: "3rem",
            }}
          >
            Upload limits of your plan exceeded. Please upgrade
          </Box>
          <Link to={URLS.teamManagement(activeTeam?.id || "")}>
            <Button variant="contained">Team Management</Button>
          </Link>
        </>
      ) : (
        <>
          <strong>Input Method</strong>
          <Box>
            Paste Text{" "}
            <Switch checked={useFile} onChange={(ev) => setUseFile(!useFile)} />{" "}
            Upload File
          </Box>
          {useFile ? (
            <Box>
              <TextField
                disabled={limitExhausted}
                type="file"
                onChange={handleFileChange}
                variant="outlined"
                fullWidth
                sx={{ mb: 2 }}
              />
              {selectedFile && (
                <Box>
                  <Typography variant="body1">
                    Selected File: {selectedFile.name}
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleRemoveFile}
                    sx={{ mt: 1 }}
                  >
                    Remove File
                  </Button>
                </Box>
              )}
              {errors?.file?.map((error) => (
                <Typography sx={{ color: "red" }}>{error}</Typography>
              ))}

              <Typography>
                Filetypes supported{" "}
                <code>
                  .txt, .md, .markdown, .jpg, .jpeg, .png, .webp, .csv, .html,
                  .pdf, .xls, .xlsx, .doc, .docx, .ppt, .pptx
                </code>
              </Typography>
            </Box>
          ) : (
            <Box>
              <TextareaAutosize
                aria-label="minimum height"
                minRows={10}
                placeholder=""
                style={{ width: "100%" }}
                value={fileText}
                onChange={(ev) => setFileText(ev.target.value)}
              />
              {errors?.fileText?.map((error) => (
                <Typography sx={{ color: "red" }}>{error}</Typography>
              ))}
            </Box>
          )}
          <FormControl fullWidth margin="dense">
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <strong>Mode</strong>
              <Tooltip title="[REQUIRED] Define how you want extractions to be made and linked. Some modes are limited by your teams current subsciption.">
                <HelpCenterIcon></HelpCenterIcon>
              </Tooltip>
            </Box>
            <Typography>
              Standard mode is designed to create basic extractions. AI mode
              offers advanced extractions and can link extractions together
              using any relationships described in the text of the report.
            </Typography>
            <Select
              name="relationship_mode"
              value={formData.relationship_mode}
              onChange={(e) =>
                setFormData({ ...formData, relationship_mode: e.target.value })
              }
            >
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem
                disabled={!activeTeam?.allowed_ai_relationships}
                value="ai"
              >
                AI
              </MenuItem>
            </Select>
            {errors?.relationship_mode?.map((error) => (
              <Typography sx={{ color: "red" }}>{error}</Typography>
            ))}
          </FormControl>

          <>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={
                        !activeTeam?.allowed_attack_flow ||
                        formData.relationship_mode !== "ai"
                      }
                      checked={
                        formData.relationship_mode === "ai" &&
                        formData.ai_create_attack_flow
                      }
                      onChange={(ev) =>
                        setFormData({
                          ...formData,
                          ai_create_attack_flow: ev.target.checked,
                        })
                      }
                    />
                  }
                  label="Create an Attack Flow"
                />
              </FormControl>
              <Tooltip title="[OPTIONAL] Create an Attack Flow to model the order in which the ATT&CK Tactics and Techniques are described in the report. You must use AI mode for this setting to work. Limited by the subscription of your team.">
                <HelpCenterIcon></HelpCenterIcon>
              </Tooltip>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={
                        !activeTeam?.allowed_extract_text_from_image ||
                        formData.relationship_mode !== "ai"
                      }
                      checked={
                        formData.relationship_mode === "ai" &&
                        formData.extract_text_from_image
                      }
                      onChange={(ev) =>
                        setFormData({
                          ...formData,
                          extract_text_from_image: ev.target.checked,
                        })
                      }
                    />
                  }
                  label="Extract Text From Image"
                />
              </FormControl>
              <Tooltip title="[OPTIONAL] Extract text from images found in the report so that extractions can be made from the text. You must use AI mode for this setting to work. Limited by the subscription of your team.">
                <HelpCenterIcon></HelpCenterIcon>
              </Tooltip>
            </Box>
          </>

          <Box mt={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <strong>Report Name</strong>
              <Tooltip title="[REQUIRED] The Report name will be used as the name property of the STIX Report object created. Is visible to members of this team and to any user in Stixify if you use the TLP:Green marking.">
                <HelpCenterIcon></HelpCenterIcon>
              </Tooltip>
            </Box>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Name"
              type="text"
              fullWidth
              value={formData.name}
              onChange={handleChange}
              disabled={limitExhausted}
              required
            />
            {errors?.name?.map((error) => (
              <Typography sx={{ color: "red" }}>{error}</Typography>
            ))}
          </Box>

          <Box mt={4}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <strong>Labels</strong>
              <Tooltip title="[OPTIONAL] Labels categorise Reports making them easier to search for in Stixify. The values entered will be used in the label property of the STIX Report object created. Is visible to members of this team and to any user in Stixify if you use the TLP:Green marking. You can enter as many labels as you need by clicking the Add button.">
                <HelpCenterIcon></HelpCenterIcon>
              </Tooltip>
            </Box>
            <LabelInput
              onValueChange={(value) =>
                setFormData({ ...formData, labels: value })
              }
            ></LabelInput>
          </Box>

          <Box marginY={2}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <strong>Confidence Score</strong>
              <Tooltip title="[REQUIRED] Confidence represents how confident you are in the accuracy of this Report. Confidence is scored between 0 and 100 where 0 is unknown, 1 is lowest confidence, and 100 is highest confidence.">
                <HelpCenterIcon></HelpCenterIcon>
              </Tooltip>
            </Box>
            <Slider
              disabled={limitExhausted}
              sx={{ marginTop: "2rem" }}
              aria-label="ios slider"
              valueLabelDisplay="on"
              value={formData.confidence}
              onChange={(ev, value) => setConfidence(value)}
              defaultValue={50}
            />
            {errors?.confidence?.map((error) => (
              <Typography sx={{ color: "red" }}>{error}</Typography>
            ))}
          </Box>

          <Box mb={2}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <strong>TLP Level</strong>
              <Tooltip title="[REQUIRED] TLP defines who can see the Report inside Stixify. Reports marked TLP:Green are visible to all Stixify users. Reports marked TLP:Amber means the uploaded Report will be visible to only member of your team.">
                <HelpCenterIcon></HelpCenterIcon>
              </Tooltip>
            </Box>
            <Select
              sx={{ width: "100%" }}
              label="TLP Level"
              name="tlp_level"
              value={formData.tlp_level}
              disabled={limitExhausted}
              onChange={(e) =>
                setFormData({ ...formData, tlp_level: e.target.value })
              }
            >
              {TLP_LEVELS.map((extraction) => (
                <MenuItem key={extraction} value={extraction}>
                  {extraction}
                </MenuItem>
              ))}
            </Select>
            {errors?.tlp_level?.map((error) => (
              <Typography sx={{ color: "red" }}>{error}</Typography>
            ))}
          </Box>

          {errors?.non_field_errors?.map((error) => (
            <Typography sx={{ color: "red" }}>{error}</Typography>
          ))}
          <Button
            variant="contained"
            sx={{ textTransform: "uppercase" }}
            onClick={onCancel}
            color="secondary"
          >
            Cancel
          </Button>
          <LoadingButton
            disabled={disableButton}
            isLoading={loading}
            variant="contained"
            sx={{ textTransform: "uppercase", marginLeft: "3rem" }}
            onClick={handleSubmit}
            color="primary"
          >
            {entryData ? "Update" : "Add"}
          </LoadingButton>
        </>
      )}
    </Box>
  );
};

export default ReportForm;
